import { GridColDef } from '@mui/x-data-grid-pro';
import { isDriverSelectable } from './types';
import CheckCircleIcon from '@mui/icons-material/CheckCircleTwoTone';
import { caseInsensitiveIncludes } from '../Dispatch/columns';
import MobileDriverItem from './MobileDriverItem';
import ErrorOutlineTwoTone from '@mui/icons-material/ErrorOutlineTwoTone';
import { Tooltip } from '@mui/material';
import { ExtendedDriver } from '@hooks/useDriverTractors.types';
import { DateAfterTodayOperator } from '../Filters';
import { ParsedDate } from '@api/Trinium/types';
import StackedTextCell from '../ColumnDefinitions/StackedTextCell';
import RenderHeader from '../RenderHeader';

import styles from './DataGrid.Driver.module.css';
import cellStyles from '../RenderCell.module.css';

export function getDriverColumnDefinitions(
  applyDefaultTractorValidation: boolean,
  requiresHazmatCert: boolean
): GridColDef<ExtendedDriver>[] {
  return [
    {
      field: 'id',
      minWidth: 95,
      width: 95,
      headerName: 'Code',
      headerClassName: styles['grid-column-header'],
      sortable: false,
      disableReorder: true,
      getApplyQuickFilterFn: (value: string) => {
        return (_, row): boolean => {
          return caseInsensitiveIncludes(value, row.id);
        };
      },
      renderCell: ({ row }) => {
        const selectionStatus = isDriverSelectable(row, requiresHazmatCert, applyDefaultTractorValidation);

        return (
          <div
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <span>{row.id}</span>
            {!selectionStatus.isSelectable && (
              <Tooltip
                title={
                  <ul className={styles['disabled-reasons-ul']}>
                    {selectionStatus.disabledReason.map((reason, index) => (
                      <li key={`disabled-reason-${String(index)}`}>{reason}</li>
                    ))}
                  </ul>
                }>
                <ErrorOutlineTwoTone className={styles['disabled-warning-icon']} />
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      field: 'name',
      minWidth: 145,
      flex: 1,
      headerName: 'Name',
      headerClassName: styles['grid-column-header'],
      sortable: false,
      disableReorder: true
    },
    {
      field: 'driveBreak',
      headerClassName: styles['grid-column-header'],
      renderHeader: () => (
        <div className={styles['stacked']}>
          <span>Drive</span>
          <span>Break</span>
        </div>
      ),
      renderCell: ({ row }) => (
        <div className={styles['stacked']}>
          <span>{row.drvnHrs}</span>
          <span>{row.brkCntdwn}</span>
        </div>
      ),
      minWidth: 57,
      width: 57,
      sortable: false,
      disableReorder: true
    },
    {
      field: 'shiftCycle',
      headerClassName: styles['grid-column-header'],
      renderHeader: () => (
        <div className={styles['stacked']}>
          <span>Shift</span>
          <span>Cycle</span>
        </div>
      ),
      renderCell: ({ row }) => (
        <div className={styles['stacked']}>
          <span>{row.shftHrs}</span>
          <span>{row.cycleHrs}</span>
        </div>
      ),
      minWidth: 57,
      width: 57,
      sortable: false,
      disableReorder: true
    },
    {
      field: 'tractorId',
      minWidth: 130,
      headerClassName: styles['grid-column-header'],
      headerName: 'Current Truck',
      renderCell: ({ row }) => {
        if (row.tractors.length === 1) {
          const defaultTractor = row.tractors.find((d) => d.id === row.tractorId);
          if (defaultTractor) {
            return <span>{defaultTractor.id}</span>;
          }
        }
        return row.tractors.length > 0 ? (
          <span
            title='Multiple Tractors Available'
            style={{ width: '100%', fontStyle: 'italic' }}>
            *
          </span>
        ) : (
          <></>
        );
      }
    },
    {
      field: 'pltNo',
      headerClassName: styles['grid-column-header'],
      headerName: 'Plate #',
      renderCell: ({ row }) => {
        if (row.tractors.length === 1) {
          const defaultTractor = row.tractors.find((d) => d.id === row.tractorId);
          if (defaultTractor) {
            return <span>{defaultTractor.pltNo}</span>;
          }
        }
        return row.tractors.length > 0 ? (
          <span
            title='Multiple Tractors Available'
            style={{ width: '100%', fontStyle: 'italic' }}>
            *
          </span>
        ) : (
          <></>
        );
      }
    },
    {
      field: 'hazmatExpirationDate',
      minWidth: 120,
      width: 120,
      headerName: 'Hazmat Exp.',
      headerClassName: styles['grid-column-header'],
      renderCell: ({ row }) => <span title={row.hazmatExpirationDate?.date}>{row.hazmatExpirationDate?.shortDateExtended}</span>,
      align: 'center',
      sortable: false,
      disableReorder: true,
      valueGetter: (value?: ParsedDate) => {
        return value?.date;
      },
      filterOperators: [DateAfterTodayOperator]
    },
    {
      field: 'isTankerEndorsed',
      minWidth: 48,
      width: 48,
      headerName: 'Tnk',
      headerClassName: styles['grid-column-header'],
      renderHeader: () => (
        <div
          title='Tanker Endorsement'
          className={styles['stacked']}>
          <span>Tnk</span>
        </div>
      ),
      renderCell: ({ row }) => {
        return row.isTankerEndorsed ? (
          <CheckCircleIcon
            fontSize={'small'}
            className={styles['has-elog-ico']}
          />
        ) : (
          <></>
        );
      },
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'cdlExpiration',
      minWidth: 120,
      width: 120,
      renderHeader: () => (
        <RenderHeader
          primaryText='CDL No.'
          secondaryText='Expiration'
        />
      ),
      cellClassName: cellStyles['cell'],
      renderCell: ({ row }) => (
        <StackedTextCell
          primaryText={row.cdlNo || '-'}
          secondaryText={row.cdlExpirationDate?.shortDateExtended}
        />
      ),
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'medicalExpiration',
      minWidth: 120,
      width: 120,
      headerName: 'Medical Exp.',
      headerClassName: styles['grid-column-header'],
      renderCell: ({ row }) => <span title={row.medExpirationDate?.date}>{row.medExpirationDate?.shortDateExtended}</span>,
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'elog',
      minWidth: 75,
      width: 75,
      headerName: 'E-Log',
      headerClassName: styles['grid-column-header'],
      renderCell: ({ row }) =>
        row.hasELogs ? (
          <CheckCircleIcon
            fontSize={'small'}
            className={styles['has-elog-ico']}
          />
        ) : (
          <></>
        ),
      align: 'center',
      sortable: false,
      disableReorder: true
    },
    {
      field: 'isAssignedJob',
      filterable: true,
      valueGetter: (value: boolean) => String(value)
    },
    {
      field: 'isOnLastJob',
      filterable: true
    }
  ];
}

export function getDriverMobileColumnDefinitions(
  applyDefaultTractorValidation: boolean,
  requiresHazmatCert: boolean
): GridColDef<ExtendedDriver>[] {
  return [
    {
      field: 'Mobile Card',
      flex: 1,
      renderCell: ({ row }) => (
        <MobileDriverItem
          key={row.id}
          driver={row}
          applyDefaultTractorValidation={applyDefaultTractorValidation}
          requiresHazmatCert={requiresHazmatCert}
        />
      ),
      getApplyQuickFilterFn: (value: string) => {
        return (_, row): boolean => {
          const { id, name } = row;
          const searchableFields = [id, name];
          const defaultTractor = row.tractors.find((d) => d.id === row.tractorId);
          if (defaultTractor && row.tractors.length === 1) {
            searchableFields.push(defaultTractor.id);
          }
          return caseInsensitiveIncludes(value, searchableFields);
        };
      }
    },
    {
      field: 'hazmatExpirationDate',
      valueGetter: (value?: ParsedDate) => {
        return value?.date;
      },
      filterOperators: [DateAfterTodayOperator]
    },
    {
      field: 'isAssignedJob',
      filterable: true,
      valueGetter: (value: boolean) => String(value)
    },
    {
      field: 'isOnLastJob',
      filterable: true
    }
  ];
}
